import { Fragment, useEffect, useState } from 'react';
import { can, formatJalali, formatRial, tehranToday } from '@kia/shared';
import { api, errMsg } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useApiResource } from '../lib/useApiResource';
import {
  Button,
  Card,
  Chip,
  EntityPicker,
  ErrorRetry,
  Field,
  Input,
  Kpi,
  Modal,
  Money,
  useToast,
  NumberInput,
  JalaliDateInput,
  Textarea,
} from '../ui';
import { CsvImportModal } from './CsvImportModal';
import { shouldPromptPriceDuty } from './priceDutyView';

interface Supplier {
  id: number;
  name: string;
  phone: string;
  version: number;
}
interface MaterialHit {
  code: string;
  name: string;
  effectivePrice: number | null;
  source: 'actual' | 'group' | null;
  priceAgeDays: number | null;
  stale: boolean;
}

/** TKT-8a: the price-health a PO line inherits from the book price it was prefilled with. */
interface PriceHealth {
  effectivePrice: number | null;
  source: 'actual' | 'group' | null;
  priceAgeDays: number | null;
  stale: boolean;
}

/** A soft price-health chip, shown ONLY when the book price is a concern (missing or stale) — a fresh,
 *  dated price gets no chip (freshness-as-noise avoidance, mirroring the deal-margin stamps). */
function PriceHealthChip({ h }: { h: PriceHealth }) {
  if (h.effectivePrice == null) return <Chip tone="danger">بدون قیمت</Chip>;
  if (h.stale) {
    return (
      <Chip tone="warn">
        {h.priceAgeDays == null ? 'قیمت بدون تاریخ' : `کهنه · ${h.priceAgeDays} روز`}
      </Chip>
    );
  }
  return null;
}
interface PoListRow {
  id: number;
  no: string;
  state: string;
  supplier_name: string | null;
  created_at: string;
  created_by: string;
  total: number;
  item_count: number;
  last_receipt: string | null;
}
interface PoItem {
  material_code: string;
  qty: number;
  price: number;
}
interface LoadedPo extends PoListRow {
  version: number;
  items: PoItem[];
  receipts: {
    /** receipt id — drives the per-receipt «ثبت قیمت» correction action (BIDX-P1) */ id: number;
    material_code: string;
    qty: number;
    price_paid: number;
    date: string;
    price_estimated: number;
  }[];
}
interface InventoryRow {
  material_code: string;
  name: string;
  unit: string;
  qtyReceived: number;
  /** of qtyReceived, the quantity received at an UNCONFIRMED price (out of the money columns) */
  qtyUnpriced: number;
  avgPaid: number | null;
  currentPrice: number | null;
  holdingGain: number | null;
  valueAtCurrent: number | null;
  lastDate: string;
  lastSupplier: string;
  lastPricePaid: number | null;
  lastPriceEstimated: boolean;
}
interface PurchaseRow {
  id: number;
  date: string;
  po_no: string;
  supplier: string;
  material_code: string;
  name: string;
  unit: string;
  qty: number;
  price_paid: number;
  amount: number;
  created_by: string;
  /** set when this receipt was reversed (E16-19); '' = live */ reversed_at: string;
  reversed_by: string;
  /** 1 = `price_paid` is the PO quote, not confirmed paid money (E15-07) */ price_estimated: number;
}
interface Cash {
  pendingIn: number;
  pendingOut: number;
  net: number;
  overdueIn: number;
  overdueOut: number;
}
interface CashFlowBucket {
  key: string;
  label: string;
  inflowExact: number;
  inflowEstimated: number;
  outflowExact: number;
  outflowEstimated: number;
  net: number;
  cumulative: number;
}
interface CashFlow {
  buckets: CashFlowBucket[];
  monthlyOpexEstimate: number;
  opexBasis: 'recurring' | 'weighted-average' | 'config-fallback' | 'none';
  opexMonths: number;
  exactInCount: number;
  exactOutCount: number;
}

const PO_STATE_FA: Record<string, string> = {
  Draft: 'پیش‌نویس',
  Ordered: 'سفارش‌شده',
  Received: 'دریافت‌شده',
  Cancelled: 'لغو',
};
const PO_NEXT: Record<string, string[]> = {
  Draft: ['Ordered', 'Cancelled'],
  Ordered: ['Received', 'Cancelled'],
};

/**
 * DUTY-1: the weekly raw-price freshness duty, surfaced as an imperative on the price-writer's own landing.
 * Purchasing lands here (`navModel PREFERRED_LANDING`) and otherwise never sees the passive PriceDutyCard
 * (buried behind the «وظیفهٔ هفتگی» tab on Prices, which they don't open). Reads the ONE authority
 * (`/api/price-duty` → `priceDutyStatus`) — no new metric, no new signal engine. Shown only to those who
 * CAN act (`price.write`) and only when the duty is genuinely pending (materials past the stale window);
 * a fresh book shows nothing (freshness-as-noise avoidance, matching the per-line PriceHealthChip).
 */
function PriceDutyPromptCard() {
  const { user } = useAuth();
  const canWritePrice = user ? can(user.role, 'price.write') : false;
  const { data } = useApiResource<{
    staleMaterials: number;
    staleDays: number;
    oldestPriceAgeDays: number | null;
  }>('/api/price-duty');
  if (!shouldPromptPriceDuty(canWritePrice, data)) return null;
  return (
    <div className="banner banner-warn" style={{ marginBottom: 12 }} role="status">
      <b>قیمت مواد به‌روزرسانی می‌خواهد.</b> {data.staleMaterials} ماده قدیمی‌تر از {data.staleDays}{' '}
      روز است
      {data.oldestPriceAgeDays != null ? ` (کهنه‌ترین: ${data.oldestPriceAgeDays} روز)` : ''} —
      به‌روزرسانی با شماست.
    </div>
  );
}

export function BuyScreen() {
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'purchase.write') : false;
  const canPay = user ? can(user.role, 'cheque.write') : false;
  return (
    <>
      <PriceDutyPromptCard />
      <CashCard />
      <CashFlowCard />
      <SupplierPayablesCard canPay={canPay} />
      <PosCard canWrite={canWrite} />
      <PurchasesCard />
      <InventoryCard />
      <SuppliersCard canWrite={canWrite} />
    </>
  );
}

interface SupplierPayableRow {
  supplierId: number | null;
  supplierName: string | null;
  total: number;
  count: number;
  nextDue: string | null;
  pastDue: number;
}

/**
 * TKT-8b — the buy-side payables worklist: who we owe, how much, next due, and the past-due portion, with
 * an inline "record payment" that posts an out-cheque against the supplier (same /api/cheques route a human
 * uses). Reuses the ONE payables projection server-side, so it agrees with the cash calendar.
 */
function SupplierPayablesCard({ canPay }: { canPay: boolean }) {
  const toast = useToast();
  const { data, status, error, reload } = useApiResource<SupplierPayableRow[]>('/api/pos/payables');
  const rows = data ?? [];
  const [payFor, setPayFor] = useState<number | null>(null);
  const [amount, setAmount] = useState('');
  const [due, setDue] = useState(tehranToday());
  const [busy, setBusy] = useState(false);

  function openPay(r: SupplierPayableRow) {
    setPayFor(r.supplierId);
    setAmount(String(r.total));
    setDue(tehranToday());
  }
  async function submitPay(supplierId: number) {
    const amt = Number(amount);
    if (!(amt > 0)) return toast('مبلغ نامعتبر', 'error');
    setBusy(true);
    const r = await api('/api/cheques', {
      method: 'POST',
      body: JSON.stringify({
        direction: 'out',
        kind: 'cheque',
        supplier_id: supplierId,
        amount: amt,
        due_date: due,
        status: 'pending',
      }),
    });
    setBusy(false);
    if (!r.ok) return toast(errMsg(r), 'error');
    toast('چک پرداختی ثبت شد');
    setPayFor(null);
    reload();
  }

  return (
    <Card title="پرداختنی تأمین‌کنندگان (برآوردی از شرایط خرید)">
      {status === 'error' ? (
        <ErrorRetry error={error} onRetry={() => reload()} />
      ) : status === 'loading' ? (
        <p className="muted">در حال بارگذاری…</p>
      ) : rows.length === 0 ? (
        <p className="muted">پرداختنی برآوردی‌ای وجود ندارد.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>تأمین‌کننده</th>
              <th>مبلغ (﷼)</th>
              <th>نزدیک‌ترین سررسید</th>
              <th>سررسیدگذشته</th>
              {canPay ? <th></th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Fragment key={r.supplierId ?? 'none'}>
                <tr>
                  <td>{r.supplierName ?? 'بدون تأمین‌کننده'}</td>
                  <td className="num">
                    <Money value={r.total} />
                  </td>
                  <td>{r.nextDue ? formatJalali(r.nextDue) : '—'}</td>
                  <td className="num">
                    {r.pastDue > 0 ? <Chip tone="danger">{formatRial(r.pastDue)}</Chip> : '—'}
                  </td>
                  {canPay ? (
                    <td>
                      {r.supplierId != null ? (
                        <Button variant="secondary" onClick={() => openPay(r)}>
                          ثبت پرداخت
                        </Button>
                      ) : null}
                    </td>
                  ) : null}
                </tr>
                {canPay && payFor === r.supplierId && r.supplierId != null ? (
                  <tr>
                    <td colSpan={5}>
                      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                        <NumberInput
                          placeholder="مبلغ چک"
                          value={amount}
                          onValue={(n) => setAmount(n ? String(n) : '')}
                        />
                        <JalaliDateInput value={due} onValue={setDue} />
                        <Button
                          onClick={() => submitPay(r.supplierId!)}
                          loading={busy}
                          disabled={busy}
                        >
                          ثبت چک پرداختی
                        </Button>
                        <Button variant="secondary" onClick={() => setPayFor(null)}>
                          انصراف
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/**
 * Cash-flow forecast (DEBT-FC): the SAME authoritative money-event stream the cash calendar runs on
 * (cheques + loan installments = exact; deposits + deal/PO terms + recurring/opex = estimated), just
 * re-bucketed by horizon instead of by month — so this card and the cash calendar can never disagree.
 */
function CashFlowCard() {
  const { data: cf, status, error, reload } = useApiResource<CashFlow>('/api/cashflow');
  const opexNote = !cf
    ? ''
    : cf.opexBasis === 'recurring'
      ? 'برآورد هزینهٔ عملیاتی از الگوهای «هزینهٔ ثابت» ثبت‌شده (به‌علاوهٔ باقیماندهٔ میانگین وزنی).'
      : cf.opexBasis === 'weighted-average'
        ? `برآورد هزینهٔ ماهانه از میانگین وزنی ${cf.opexMonths} ماه اخیر: ${formatRial(cf.monthlyOpexEstimate)}`
        : cf.opexBasis === 'config-fallback'
          ? `برآورد هزینهٔ ماهانه از اهرم «هزینهٔ عمومی»: ${formatRial(cf.monthlyOpexEstimate)}`
          : 'تاریخچهٔ بستن ماه یا اهرم هزینه هنوز وارد نشده — تخمین هزینه صفر است.';
  return (
    <Card title="پیش‌بینی جریان نقدی (همان رویدادهای تقویم نقدینگی، بر اساس بازهٔ سررسید)">
      {status === 'error' ? (
        <ErrorRetry error={error} onRetry={() => reload()} />
      ) : status === 'loading' || !cf ? (
        <p className="muted">در حال بارگذاری…</p>
      ) : (
        <>
          <p className="muted" style={{ marginTop: 0 }}>
            چک‌ها و اقساط وام «دقیق»؛ پیش‌دریافت‌ها، اقساط قراردادها، پرداختنی تأمین‌کننده و
            هزینه‌های ثابت «تخمینی» — همان منبع تقویم نقدینگی. {opexNote}
          </p>
          <table className="table">
            <thead>
              <tr>
                <th>بازه</th>
                <th>دریافتنی (دقیق)</th>
                <th>دریافتنی (تخمینی)</th>
                <th>پرداختنی (دقیق)</th>
                <th>هزینه/پرداختنی تخمینی</th>
                <th>خالص</th>
                <th>تجمعی</th>
              </tr>
            </thead>
            <tbody>
              {cf.buckets.map((b) => (
                <tr key={b.key}>
                  <td>{b.label}</td>
                  <td className="num" style={{ color: 'var(--green)' }}>
                    {b.inflowExact ? formatRial(b.inflowExact) : '—'}
                  </td>
                  <td className="num muted" style={{ color: 'var(--green)' }}>
                    {b.inflowEstimated ? formatRial(b.inflowEstimated) : '—'}
                  </td>
                  <td className="num" style={{ color: 'var(--red)' }}>
                    {b.outflowExact ? formatRial(b.outflowExact) : '—'}
                  </td>
                  <td className="num muted">
                    {b.outflowEstimated ? formatRial(b.outflowEstimated) : '—'}
                  </td>
                  <td className="num" style={{ color: b.net >= 0 ? 'var(--green)' : 'var(--red)' }}>
                    {formatRial(b.net)}
                  </td>
                  <td
                    className="num"
                    style={{
                      fontWeight: 700,
                      color: b.cumulative >= 0 ? 'var(--green)' : 'var(--red)',
                    }}
                  >
                    {formatRial(b.cumulative)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Card>
  );
}

function CashCard() {
  const { data: cash, status, error, reload } = useApiResource<Cash>('/api/cash');
  return (
    <Card title="وضعیت نقدی (چک‌های در جریان)">
      {status === 'error' ? (
        <ErrorRetry error={error} onRetry={() => reload()} />
      ) : status === 'loading' || !cash ? (
        <p className="muted">در حال بارگذاری…</p>
      ) : (
        <>
          <Kpi label="دریافتنی در جریان" value={<Money value={cash.pendingIn} />} />
          <Kpi label="پرداختنی در جریان" value={<Money value={cash.pendingOut} />} />
          <Kpi
            label="خالص"
            value={<Money value={cash.net} />}
            tone={cash.net < 0 ? 'bad' : 'good'}
          />
          {cash.overdueIn > 0 ? (
            <Kpi label="دریافتنی سررسیدگذشته" value={<Money value={cash.overdueIn} />} tone="bad" />
          ) : null}
          {cash.overdueOut > 0 ? (
            <Kpi
              label="پرداختنی سررسیدگذشته"
              value={<Money value={cash.overdueOut} />}
              tone="bad"
            />
          ) : null}
        </>
      )}
    </Card>
  );
}

function SuppliersCard({ canWrite }: { canWrite: boolean }) {
  const toast = useToast();
  const { data, status, error, reload } = useApiResource<Supplier[]>('/api/suppliers');
  const list = data ?? [];
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [importOpen, setImportOpen] = useState(false);

  async function add() {
    if (!name.trim()) return toast('نام تأمین‌کننده لازم است', 'error');
    const r = await api('/api/suppliers', {
      method: 'POST',
      body: JSON.stringify({ name: name.trim(), phone: phone.trim() }),
    });
    if (!r.ok) return toast(errMsg(r), 'error');
    toast('تأمین‌کننده افزوده شد');
    setName('');
    setPhone('');
    reload();
  }

  return (
    <Card title={`تأمین‌کنندگان (${list.length})`}>
      {canWrite ? (
        <div className="row" style={{ marginBottom: 10 }}>
          <div style={{ flex: 2 }}>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="نام تأمین‌کننده"
            />
          </div>
          <div style={{ flex: 1 }}>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="تلفن" />
          </div>
          <Button variant="secondary" onClick={add}>
            افزودن
          </Button>
          <Button variant="secondary" onClick={() => setImportOpen(true)}>
            ⬆ ورود گروهی (CSV)
          </Button>
        </div>
      ) : null}
      {status === 'error' ? (
        <ErrorRetry error={error} onRetry={() => reload()} />
      ) : status === 'loading' ? (
        <p className="muted">در حال بارگذاری…</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>نام</th>
              <th>تلفن</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td className="empty" colSpan={2}>
                  هنوز تأمین‌کننده‌ای ثبت نشده.
                </td>
              </tr>
            ) : (
              list.map((s) => (
                <tr key={s.id}>
                  <td>{s.name}</td>
                  <td>{s.phone || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
      {importOpen ? (
        <CsvImportModal
          title="ورود گروهی تأمین‌کنندگان (CSV)"
          hint="ستون‌ها: نام (لازم)، کد، شماره اقتصادی، شناسه ملی، تلفن، ایمیل، نشانی، شرایط، یادداشت"
          previewUrl="/api/suppliers/import/preview"
          commitUrl="/api/suppliers/import"
          onClose={() => setImportOpen(false)}
          onDone={() => {
            setImportOpen(false);
            reload();
          }}
        />
      ) : null}
    </Card>
  );
}

/**
 * «برآوردی» — this receipt's price is the PO QUOTE, not confirmed paid money (E15-07). ONE definition, used
 * on BOTH receipt surfaces: the PO-detail sub-table and the global purchase ledger. It lived only on the
 * former, so the same row read "estimated — verify me" in one table and "paid" in the other.
 */
function EstimatedPriceChip({ on }: { on: number | boolean }) {
  if (!on) return null;
  return (
    <span
      aria-label="قیمت برآوردی — تأییدنشده"
      style={{
        marginRight: 6,
        fontSize: 11,
        color: 'var(--amber, #b58900)',
        border: '1px solid var(--amber, #b58900)',
        borderRadius: 4,
        padding: '0 4px',
      }}
    >
      برآوردی
    </span>
  );
}

/**
 * The «برآوردی» EXPLANATION, on screen (BIDX-P4). It used to live entirely in the chip's `title=`
 * attribute — invisible on touch, never printed, and not primary content for a screen reader. So the one
 * surface that knew a number was a guess only said so on hover, on a desktop, if you happened to point at it.
 *
 * Rendered once per receipts table rather than per row: repeating a sentence on every row is noise, and the
 * chip itself already marks WHICH rows. «تأیید کنید» now names the action that exists (BIDX-P1's «ثبت قیمت»
 * button, on this same row) instead of instructing the reader to do something the product could not do.
 */
function EstimatedPriceLegend({ on, canPrice }: { on: boolean; canPrice: boolean }) {
  if (!on) return null;
  return (
    <p style={{ margin: '6px 0 0', color: 'var(--ink-3)', fontSize: '.85em', lineHeight: 1.9 }}>
      <span style={{ color: 'var(--amber, #b58900)' }}>برآوردی</span> = قیمت پرداختی هنگام ثبت
      دریافت وارد نشد و از قیمت سفارش برآورد شد. چنین رسیدی وارد دفاتر نمی‌شود (نه سند حسابداری، نه
      «پرداختنی»، نه ارزش‌گذاری موجودی)؛ کالایش اما در انبار شمرده می‌شود. «دستور ساخت»ی که به آن
      ماده نیاز دارد تکمیل نمی‌شود اگر مقدار موردنیازش از موجودیِ دارای قیمت تأییدشدهٔ همان ماده
      بیشتر باشد؛ اگر مقدار موردنیاز داخل همان موجودیِ تأییدشده جا شود، دستور ساخت مثل همیشه تکمیل
      می‌شود.{' '}
      {/* P4-REPAIR: this legend rendered UNGATED while the «ثبت قیمت» button it names is gated on
          purchase.write — so a Viewer/Finance reader saw the instruction beside an empty action column.
          `canPrice` is the SAME predicate the button is gated on at both call sites, so the sentence and
          the control can never disagree. */}
      {canPrice ? (
        <>با «ثبت قیمت» روی همان ردیف، قیمت واقعی را تأیید کنید.</>
      ) : (
        <>
          تأیید با «ثبت قیمت» روی همان ردیف انجام می‌شود؛ اگر دسترسی خرید ندارید، از واحد خرید
          بخواهید آن را ثبت کند.
        </>
      )}
    </p>
  );
}

/** The purchase ledger: every receipt — what, when, from whom, at what price. */
function PurchasesCard() {
  const { user } = useAuth();
  const canReverse = user ? can(user.role, 'purchase.write') : false;
  const [rows, setRows] = useState<PurchaseRow[]>([]);
  const [q, setQ] = useState('');
  const [reverseFor, setReverseFor] = useState<PurchaseRow | null>(null);
  const [priceFor, setPriceFor] = useState<PurchaseRow | null>(null);
  const load = (query = q) => {
    void api<PurchaseRow[]>(`/api/purchases?q=${encodeURIComponent(query)}`).then((r) => {
      if (r.ok && Array.isArray(r.body)) setRows(r.body as PurchaseRow[]);
    });
  };
  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <Card title={`تاریخچهٔ خرید (${rows.length})`}>
      <div className="row" style={{ marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="جستجو: ماده، تأمین‌کننده، شماره سفارش…"
          />
        </div>
      </div>
      {rows.length === 0 ? (
        // BIDX-P4 (UX-2): the old sentence promised «قیمت پرداختی وارد بهای مواد می‌شود» unconditionally.
        // That only happens when the operator actually types a paid price. BIDX-P5 then made the blank case
        // stricter still — the quote no longer lands in the material's cost either, it only sits on the
        // receipt, flagged «برآوردی». Say what actually happens in BOTH branches.
        <p className="muted" style={{ marginBottom: 0 }}>
          هنوز دریافتی ثبت نشده — با «ثبت دریافت» روی سفارش خرید، اینجا پر می‌شود. اگر قیمت پرداختی
          را وارد کنید، همان قیمت وارد بهای مواد می‌شود؛ اگر خالی بماند، بهای مواد تغییر نمی‌کند و
          رسید فقط «برآوردی» علامت می‌خورد.
        </p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>تاریخ</th>
              <th>سفارش</th>
              <th>تأمین‌کننده</th>
              <th>ماده</th>
              <th>مقدار</th>
              <th>قیمت پرداختی (﷼)</th>
              <th>مبلغ (﷼)</th>
              <th>ثبت‌کننده</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const reversed = !!r.reversed_at;
              const strike = reversed ? { textDecoration: 'line-through' as const } : undefined;
              return (
                <tr key={r.id} style={reversed ? { opacity: 0.5 } : undefined}>
                  <td className="num">{r.date || '—'}</td>
                  <td className="num">{r.po_no}</td>
                  <td>{r.supplier || '—'}</td>
                  <td>
                    {r.name}
                    <div className="muted num">{r.material_code}</div>
                  </td>
                  <td className="num" style={strike}>
                    {r.qty} {r.unit}
                  </td>
                  <td className="num" style={strike}>
                    {formatRial(r.price_paid)}
                    {reversed ? null : <EstimatedPriceChip on={r.price_estimated} />}
                  </td>
                  <td className="num" style={strike}>
                    {formatRial(r.amount)}
                  </td>
                  <td className="muted">{r.created_by}</td>
                  <td>
                    {reversed ? (
                      <Chip tone="neutral">برگشت‌خورده</Chip>
                    ) : canReverse ? (
                      <div className="row" style={{ gap: 4 }}>
                        {r.price_estimated ? (
                          <Button variant="ghost" onClick={() => setPriceFor(r)}>
                            ثبت قیمت
                          </Button>
                        ) : null}
                        <Button variant="ghost" onClick={() => setReverseFor(r)}>
                          برگشت
                        </Button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <EstimatedPriceLegend
        on={rows.some((r) => !r.reversed_at && !!r.price_estimated)}
        canPrice={canReverse}
      />
      {reverseFor ? (
        <ReverseReceiptModal
          receipt={reverseFor}
          onClose={() => setReverseFor(null)}
          onDone={() => {
            setReverseFor(null);
            load();
          }}
        />
      ) : null}
      {priceFor ? (
        <PriceReceiptModal
          receipt={priceFor}
          onClose={() => setPriceFor(null)}
          onDone={() => {
            setPriceFor(null);
            load();
          }}
        />
      ) : null}
    </Card>
  );
}

/**
 * Reverse-entry a posted receipt (E16-19). A reason-gated modal (E13-01 irreversible-action pattern): the
 * original receipt stays on the books, an offsetting reversal is posted, and the PO line is receivable again.
 * The material's recorded paid price is NOT changed — the price-drift monitor surfaces any mismatch to review.
 */
function ReverseReceiptModal({
  receipt,
  onClose,
  onDone,
}: {
  receipt: PurchaseRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (reason.trim().length < 3) return toast('دلیل برگشت لازم است (حداقل ۳ نویسه)', 'error');
    setBusy(true);
    const r = await api(`/api/receipts/${receipt.id}/reverse`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason.trim() }),
    });
    setBusy(false);
    if (!r.ok) return toast(errMsg(r), 'error');
    toast('رسید برگشت خورد — این ردیف سفارش دوباره قابل دریافت است');
    onDone();
  }

  return (
    <Modal title="برگشت رسید خرید" onClose={onClose}>
      <div className="banner banner-warn" style={{ marginTop: 0 }}>
        این رسید <b>برگشت</b> می‌خورد: {receipt.name || receipt.material_code} × {receipt.qty}. سند
        اصلی حفظ و یک سند برگشتِ خنثی‌کننده ثبت می‌شود؛ ردیف سفارش دوباره قابل دریافت می‌شود.{' '}
        <b>قیمت خریدِ ثبت‌شدهٔ ماده تغییر نمی‌کند</b> — در صورت نیاز از «انحراف قیمت» بازبینی کنید.
      </div>
      <Field label="دلیل برگشت" required>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="مثلاً: ماده یا مقدار اشتباه ثبت شده بود"
        />
      </Field>
      <div className="row" style={{ gap: 8, marginTop: 8 }}>
        <Button variant="danger" onClick={submit} loading={busy}>
          برگشت رسید
        </Button>
        <Button variant="secondary" onClick={onClose}>
          انصراف
        </Button>
      </div>
    </Modal>
  );
}

/**
 * BIDX-P1 — confirm the real paid price of an ESTIMATED receipt. The counterpart to the reverse modal:
 * reversing removes the receipt but explicitly leaves the material's price anchor stale; confirming here is
 * the ONLY path that re-anchors it. The anchor is conditional server-side (only when this receipt OWNS it —
 * newest by business DATE among the material's live CONFIRMED receipts), and the receipt's GL entry is
 * re-booked when it had been posted at the estimate. The success toast reports what actually happened on both
 * counts rather than promising either. Mounted from BOTH receipt surfaces: the PO detail and the ledger.
 */
function PriceReceiptModal({
  receipt,
  onClose,
  onDone,
}: {
  receipt: { id: number; material_code: string; qty: number; price_paid: number };
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [price, setPrice] = useState(0);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!(price > 0)) return toast('قیمت پرداختی باید بزرگ‌تر از صفر باشد', 'error');
    setBusy(true);
    const r = await api<{ anchored: boolean; reposted: boolean }>(
      `/api/receipts/${receipt.id}/price`,
      { method: 'POST', body: JSON.stringify({ price }) },
    );
    setBusy(false);
    if (!r.ok) return toast(errMsg(r), 'error');
    const out = r.body as { anchored: boolean; reposted: boolean };
    // UX-2: say exactly what happened. The anchor is NOT skipped merely because a newer receipt exists — only
    // a newer CONFIRMED one outranks this correction (an unpriced receipt holds a quote, and a guess must not
    // beat confirmed cash). And when the receipt was already on the books at the estimate, its GL entry was
    // reversed and re-issued at the confirmed figure — the operator should know a document was produced.
    toast(
      'قیمت قطعی ثبت شد — ' +
        (out.anchored
          ? 'بهای خرید ماده با همین عدد به‌روز شد'
          : 'بهای خرید ماده تغییر نکرد، چون رسید تأییدشدهٔ جدیدتری برای این ماده ثبت شده است') +
        (out.reposted ? '؛ سند حسابداری این رسید هم با مبلغ قطعی دوباره صادر شد' : ''),
    );
    onDone();
  }

  return (
    <Modal title="ثبت قیمت قطعی رسید" onClose={onClose}>
      <div className="banner" style={{ marginTop: 0 }}>
        قیمت این رسید ({receipt.material_code} × {receipt.qty}) از قیمت سفارش <b>برآورد</b> شده بود
        (فعلاً {formatRial(receipt.price_paid)}) و به همین دلیل این رسید بیرون از دفاتر است. با ثبت
        قیمت پرداختی واقعی، نشان «برآوردی» برداشته می‌شود، رسید در همگام‌سازیِ بعدیِ دفترها به سند
        تبدیل و وارد «پرداختنی» و ارزش‌گذاری موجودی می‌شود، و — اگر این رسید به تاریخ، آخرین رسید
        تأییدشدهٔ این ماده باشد — بهای خرید ماده هم با همین عدد به‌روز می‌شود. این کار یک‌بار انجام
        می‌شود؛ برای اصلاح بعدی باید رسید را برگشت بزنید.
      </div>
      <Field label="قیمت پرداختی (﷼)" required>
        <NumberInput value={price} onValue={setPrice} min={0} />
      </Field>
      <div className="row" style={{ gap: 8, marginTop: 8 }}>
        <Button onClick={submit} loading={busy}>
          ثبت قیمت
        </Button>
        <Button variant="secondary" onClick={onClose}>
          انصراف
        </Button>
      </div>
    </Modal>
  );
}

function InventoryCard() {
  const { data, status, error, reload } = useApiResource<{
    rows: InventoryRow[];
    totalGain: number;
    unpricedMaterials: number;
  }>('/api/inventory');
  const rows = data?.rows ?? [];
  const totalGain = data?.totalGain ?? 0;
  const unpricedMaterials = data?.unpricedMaterials ?? 0;
  return (
    <Card title="موجودی خریداری‌شده — پوشش تورمی (Hedge)">
      {status === 'error' ? (
        <ErrorRetry error={error} onRetry={() => reload()} />
      ) : status === 'loading' ? (
        <p className="muted">در حال بارگذاری…</p>
      ) : rows.length === 0 ? (
        <p className="muted" style={{ marginBottom: 0 }}>
          هنوز موجودی خریداری‌شده‌ای ثبت نشده.
        </p>
      ) : (
        <>
          <div className="totalbox" style={{ marginTop: 0, marginBottom: 10 }}>
            سود نگهداری کل (خرید دیروز به قیمت امروز):{' '}
            <b style={{ color: totalGain >= 0 ? 'var(--green)' : 'var(--red)' }}>
              {formatRial(totalGain)}
            </b>
            {/* The scope travels with the number: the gain is measured only where the paid price is
                confirmed, because for an unpriced receipt we do not know what we paid. */}
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              فقط روی موجودی‌ای که «قیمت خرید تأییدشده» دارد اندازه‌گیری می‌شود.
              {unpricedMaterials > 0 ? (
                <>
                  {' '}
                  {unpricedMaterials} ماده موجودیِ با «قیمت تأییدنشده» هم دارند: مقدارش روی قفسه و
                  در «ارزش روز» هست، ولی در این عدد نیست. برای وارد شدنش، در «تاریخچهٔ خرید» روی
                  «ثبت قیمت» بزنید.
                </>
              ) : null}
            </div>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>ماده</th>
                <th>مقدار دریافتی</th>
                <th>میانگین خرید</th>
                <th>آخرین خرید</th>
                <th>قیمت امروز</th>
                <th>ارزش روز</th>
                <th>سود نگهداری</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.material_code}>
                  <td>
                    {r.name}
                    <div className="muted num">{r.material_code}</div>
                  </td>
                  <td className="num">
                    {r.qtyReceived} {r.unit}
                    {r.qtyUnpriced > 0 ? (
                      <div className="muted" style={{ fontSize: 11 }}>
                        {r.qtyUnpriced} {r.unit} با قیمت تأییدنشده
                      </div>
                    ) : null}
                  </td>
                  <td>{formatRial(r.avgPaid)}</td>
                  <td className="num">
                    {r.lastPricePaid != null ? formatRial(r.lastPricePaid) : '—'}
                    <div className="muted" style={{ fontSize: 11 }}>
                      {r.lastPriceEstimated ? 'برآوردی · ' : ''}
                      {[r.lastDate, r.lastSupplier].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </td>
                  <td>{formatRial(r.currentPrice)}</td>
                  <td className="num">
                    {r.valueAtCurrent != null ? formatRial(r.valueAtCurrent) : '—'}
                  </td>
                  <td style={{ color: (r.holdingGain ?? 0) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                    {formatRial(r.holdingGain)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Card>
  );
}

function PosCard({ canWrite }: { canWrite: boolean }) {
  const toast = useToast();
  const { data, status, error, reload } = useApiResource<PoListRow[]>('/api/pos');
  const list = data ?? [];
  const [open, setOpen] = useState<LoadedPo | null>(null);
  const [creating, setCreating] = useState(false);
  // BIDX-P1: the estimated receipt currently being priced.
  const [priceFor, setPriceFor] = useState<LoadedPo['receipts'][number] | null>(null);
  // new-PO form state
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [supplierId, setSupplierId] = useState('');
  const [expectedReceipt, setExpectedReceipt] = useState('');
  const [items, setItems] = useState<
    { material_code: string; name: string; qty: string; price: string; health: PriceHealth }[]
  >([]);
  // receive form
  const [rcQty, setRcQty] = useState('');
  const [rcPrice, setRcPrice] = useState('');
  const [rcCode, setRcCode] = useState('');
  // Receipt date — defaults to today but is editable, so a delivery that physically arrived earlier can be
  // back-dated (the server accepts it and buckets the payable/holding-gain by this date, not the entry day).
  const [rcDate, setRcDate] = useState(tehranToday());
  // in-flight guard: a double-click must not create two POs / post a receipt twice (which would feed the
  // paid price into material cost twice, corrupting avgPaid).
  const [busy, setBusy] = useState(false);

  // Refresh the supplier list whenever the create form opens (a supplier may have just been added).
  useEffect(() => {
    if (!creating) return;
    void api<Supplier[]>('/api/suppliers').then((r) => {
      if (r.ok && Array.isArray(r.body)) setSuppliers(r.body as Supplier[]);
    });
  }, [creating]);

  /** The one place the open-PO panel state is set — keeps the receive form's defaults in sync. */
  function showPo(po: LoadedPo) {
    setOpen(po);
    setRcCode(po.items[0]?.material_code ?? '');
    setRcQty('');
    setRcPrice('');
  }

  async function openPo(id: number) {
    const r = await api<LoadedPo>(`/api/pos/${id}`);
    if (r.ok) showPo(r.body as LoadedPo);
  }

  async function createPo() {
    const valid = items.filter((i) => Number(i.qty) > 0 && Number(i.price) > 0);
    if (!valid.length) return toast('حداقل یک قلم با مقدار و قیمت لازم است', 'error');
    if (busy) return;
    setBusy(true);
    try {
      const r = await api<LoadedPo>('/api/pos', {
        method: 'POST',
        body: JSON.stringify({
          supplier_id: supplierId ? Number(supplierId) : null,
          expected_receipt: expectedReceipt || undefined,
          items: valid.map((i) => ({
            material_code: i.material_code,
            qty: Number(i.qty),
            price: Number(i.price),
          })),
        }),
      });
      if (!r.ok) return toast(errMsg(r), 'error');
      toast('سفارش خرید ثبت شد');
      setCreating(false);
      setItems([]);
      setSupplierId('');
      setExpectedReceipt('');
      reload();
      showPo(r.body as LoadedPo);
    } finally {
      setBusy(false);
    }
  }

  async function transition(to: string) {
    if (!open || busy) return;
    setBusy(true);
    try {
      const r = await api<LoadedPo>(`/api/pos/${open.id}/transition`, {
        method: 'POST',
        body: JSON.stringify({ to }),
      });
      if (!r.ok) return toast(errMsg(r), 'error');
      toast(`وضعیت: ${PO_STATE_FA[to] ?? to}`);
      showPo(r.body as LoadedPo);
      reload();
    } finally {
      setBusy(false);
    }
  }

  async function receive() {
    if (!open || busy) return;
    // Immediate feedback before the server round-trip: a positive qty, and never MORE than the ordered
    // remaining (over-receipt would corrupt the material's moving-average paid price). The server enforces
    // the same rules — this just surfaces them up front.
    const item = open.items.find((it) => it.material_code === rcCode);
    const qty = Number(rcQty);
    const rem = item ? item.qty - received(rcCode) : 0;
    if (!(qty > 0)) return toast('مقدار دریافت باید بزرگ‌تر از صفر باشد', 'error');
    if (qty > rem + 1e-9) return toast(`بیش از باقی‌ماندهٔ سفارش (${rem})`, 'error');
    setBusy(true);
    try {
      const r = await api<{ po: LoadedPo }>(`/api/pos/${open.id}/receive`, {
        method: 'POST',
        body: JSON.stringify({
          material_code: rcCode,
          qty,
          price_paid: rcPrice || undefined,
          date: rcDate || undefined,
        }),
      });
      if (!r.ok) return toast(errMsg(r), 'error');
      // BIDX-P4 (UX-2): this fired UNCONDITIONALLY, so a receipt entered with the price field blank told the
      // operator the exact opposite of what happened — no paid price entered a quote did. Branch on the same
      // value the request branched on (`rcPrice || undefined` above), which is what makes the server stamp
      // price_estimated. The estimated branch names the consequence AND the one-click repair.
      toast(
        rcPrice
          ? 'دریافت ثبت شد — قیمت پرداختی وارد بهای مواد شد'
          : 'دریافت ثبت شد — قیمت پرداختی وارد نشد؛ رسید «برآوردی» علامت خورد و بهای مواد به‌روز نشد. با «ثبت قیمت» تأییدش کنید.',
      );
      // Tone stays DEFAULT on purpose: the receive genuinely succeeded, and `'error'` is the only other tone
      // Toast has. Dressing a success as an error would be the same class of lie in the other direction.
      showPo((r.body as { po: LoadedPo }).po);
      reload();
    } finally {
      setBusy(false);
    }
  }

  const received = (code: string) =>
    (open?.receipts ?? []).filter((x) => x.material_code === code).reduce((s, x) => s + x.qty, 0);
  // How much of the SELECTED line is still outstanding — drives the receive form's remaining hint + qty clamp.
  const rcItem = open?.items.find((x) => x.material_code === rcCode);
  const rcRemaining = rcItem ? rcItem.qty - received(rcCode) : 0;

  return (
    <Card title={`سفارش‌های خرید (${list.length})`}>
      {canWrite && !creating ? (
        <Button onClick={() => setCreating(true)}>+ سفارش خرید جدید</Button>
      ) : null}

      {creating ? (
        <div
          style={{ border: '1px solid var(--line)', borderRadius: 11, padding: 14, marginTop: 10 }}
        >
          <Field label="تأمین‌کننده">
            <select
              className="input"
              value={supplierId}
              onChange={(e) => setSupplierId(e.target.value)}
            >
              <option value="">— انتخاب —</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="تاریخ دریافت موردانتظار (برای زمان‌بندی پرداختنی در تقویم نقدینگی)">
            <JalaliDateInput value={expectedReceipt} onValue={setExpectedReceipt} />
          </Field>
          <EntityPicker<MaterialHit>
            label="افزودن ماده (کد یا نام)"
            endpoint="/api/materials"
            maxResults={6}
            itemKey={(m) => m.code}
            placeholder="جستجو…"
            renderResult={(m) => ({
              label: m.name,
              subtitle: (
                <span>
                  ({m.code}){m.effectivePrice != null ? ` · ${formatRial(m.effectivePrice)} ﷼` : ''}
                  <PriceHealthChip h={m} />
                </span>
              ),
            })}
            onSelect={(m) =>
              setItems((prev) => [
                ...prev,
                {
                  material_code: m.code,
                  name: m.name,
                  qty: '1',
                  price: m.effectivePrice ? String(m.effectivePrice) : '',
                  health: {
                    effectivePrice: m.effectivePrice,
                    source: m.source,
                    priceAgeDays: m.priceAgeDays,
                    stale: m.stale,
                  },
                },
              ])
            }
          />
          {items.map((it, i) => (
            <div className="row" key={i} style={{ marginTop: 6 }}>
              <span style={{ flex: 2, fontSize: 13 }}>
                {it.name} <span className="muted num">({it.material_code})</span>
                <PriceHealthChip h={it.health} />
              </span>
              <div style={{ flex: 1 }}>
                <NumberInput
                  allowDecimal
                  placeholder="مقدار"
                  value={it.qty}
                  onValue={(n) =>
                    setItems((p) =>
                      p.map((x, j) => (j === i ? { ...x, qty: n ? String(n) : '' } : x)),
                    )
                  }
                />
              </div>
              <div style={{ flex: 1 }}>
                <NumberInput
                  placeholder="قیمت واحد"
                  value={it.price}
                  onValue={(n) =>
                    setItems((p) =>
                      p.map((x, j) => (j === i ? { ...x, price: n ? String(n) : '' } : x)),
                    )
                  }
                />
              </div>
              <Button variant="danger" onClick={() => setItems((p) => p.filter((_, j) => j !== i))}>
                ✕
              </Button>
            </div>
          ))}
          <div className="row" style={{ marginTop: 10 }}>
            <Button onClick={createPo} loading={busy} disabled={busy}>
              ثبت سفارش
            </Button>
            <Button variant="secondary" onClick={() => setCreating(false)}>
              انصراف
            </Button>
          </div>
        </div>
      ) : null}

      {status === 'error' ? (
        <div style={{ marginTop: 10 }}>
          <ErrorRetry error={error} onRetry={() => reload()} />
        </div>
      ) : status === 'loading' ? (
        <p className="muted" style={{ marginTop: 10 }}>
          در حال بارگذاری…
        </p>
      ) : (
        <table className="table" style={{ marginTop: 10 }}>
          <thead>
            <tr>
              <th>شماره</th>
              <th>تأمین‌کننده</th>
              <th>تاریخ ثبت</th>
              <th>اقلام</th>
              <th>مبلغ (﷼)</th>
              <th>آخرین دریافت</th>
              <th>وضعیت</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td className="empty" colSpan={8}>
                  هنوز سفارش خریدی ثبت نشده.
                </td>
              </tr>
            ) : (
              list.map((p) => (
                <tr key={p.id}>
                  <td className="num">{p.no}</td>
                  <td>{p.supplier_name || '—'}</td>
                  <td className="num">{(p.created_at || '').slice(0, 10) || '—'}</td>
                  <td className="num">{p.item_count}</td>
                  <td className="num">{formatRial(p.total)}</td>
                  <td className="num">{p.last_receipt ? formatJalali(p.last_receipt) : '—'}</td>
                  <td>
                    <Chip
                      tone={
                        p.state === 'Received'
                          ? 'success'
                          : p.state === 'Cancelled'
                            ? 'danger'
                            : 'info'
                      }
                    >
                      {PO_STATE_FA[p.state] ?? p.state}
                    </Chip>
                  </td>
                  <td>
                    <Button variant="ghost" onClick={() => openPo(p.id)}>
                      باز کردن
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      {open ? (
        <div
          style={{ border: '1px solid var(--line)', borderRadius: 11, padding: 14, marginTop: 12 }}
        >
          <b>
            {open.no} — {PO_STATE_FA[open.state] ?? open.state}
          </b>
          <table className="table" style={{ marginTop: 8 }}>
            <thead>
              <tr>
                <th>ماده</th>
                <th>مقدار</th>
                <th>قیمت واحد</th>
                <th>مبلغ</th>
                <th>دریافت‌شده</th>
              </tr>
            </thead>
            <tbody>
              {open.items.map((it) => (
                <tr key={it.material_code}>
                  <td className="num">{it.material_code}</td>
                  <td className="num">{it.qty}</td>
                  <td>{formatRial(it.price)}</td>
                  <td className="num">{formatRial(Math.round(it.qty * it.price))}</td>
                  <td className="num">{received(it.material_code)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {open.receipts.length ? (
            <div style={{ marginTop: 8 }}>
              <b style={{ fontSize: 13 }}>دریافت‌ها</b>
              <table className="table" style={{ marginTop: 4 }}>
                <thead>
                  <tr>
                    <th>تاریخ</th>
                    <th>ماده</th>
                    <th>مقدار</th>
                    <th>قیمت پرداختی</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {open.receipts.map((rc) => (
                    <tr key={rc.id}>
                      <td className="num">{rc.date || '—'}</td>
                      <td className="num">{rc.material_code}</td>
                      <td className="num">{rc.qty}</td>
                      <td>
                        {formatRial(rc.price_paid)}
                        <EstimatedPriceChip on={rc.price_estimated} />
                      </td>
                      <td>
                        {canWrite && rc.price_estimated ? (
                          <Button variant="ghost" onClick={() => setPriceFor(rc)}>
                            ثبت قیمت
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <EstimatedPriceLegend
                on={open.receipts.some((rc) => !!rc.price_estimated)}
                canPrice={canWrite}
              />
            </div>
          ) : null}
          {canWrite && open.state === 'Ordered' ? (
            <div className="row" style={{ marginTop: 10 }}>
              <select
                className="input"
                style={{ maxWidth: 200 }}
                value={rcCode}
                onChange={(e) => setRcCode(e.target.value)}
              >
                {open.items.map((it) => (
                  <option key={it.material_code} value={it.material_code}>
                    {it.material_code}
                  </option>
                ))}
              </select>
              <div style={{ maxWidth: 130 }}>
                <NumberInput
                  allowDecimal
                  min={0}
                  max={rcRemaining}
                  placeholder="مقدار"
                  value={rcQty}
                  onValue={(n) => setRcQty(n ? String(n) : '')}
                />
                <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                  باقی‌مانده: {rcRemaining.toLocaleString('fa-IR')}
                </div>
              </div>
              {/* BIDX-P4 (UX-2): the placeholder used to TEACH the defect — «(اختیاری)» at the exact
                  keystroke where an unpriced receipt is born. It is now a labelled <Field> whose hint names
                  the consequence of leaving it blank.
                  DELIBERATELY NOT `required`: Field's own contract says `required` must be paired with
                  submit-gating so the user is LED to complete it, and this submit is NOT gated — the owner's
                  policy PERMITS an unpriced receipt (BIDX-P1 exists to price it later). A red «*» over a
                  field that submits happily blank is a UX-2 violation in the opposite direction. */}
              <div style={{ maxWidth: 210 }}>
                <Field
                  label="قیمت پرداختی (﷼)"
                  hint="خالی بماند، بهای مواد به‌روز نمی‌شود و رسید «برآوردی» علامت می‌خورد."
                >
                  <NumberInput
                    placeholder="قیمت پرداختی"
                    value={rcPrice}
                    onValue={(n) => setRcPrice(n ? String(n) : '')}
                  />
                </Field>
              </div>
              <div style={{ maxWidth: 150 }}>
                <JalaliDateInput value={rcDate} onValue={setRcDate} />
              </div>
              <Button variant="secondary" onClick={receive} loading={busy} disabled={busy}>
                ثبت دریافت
              </Button>
            </div>
          ) : null}
          {canWrite && (PO_NEXT[open.state] ?? []).length ? (
            <div className="row" style={{ marginTop: 10 }}>
              <span style={{ fontWeight: 700, fontSize: 13 }}>تغییر وضعیت:</span>
              {(PO_NEXT[open.state] ?? []).map((s) => (
                <Button key={s} variant="secondary" onClick={() => transition(s)}>
                  {PO_STATE_FA[s] ?? s}
                </Button>
              ))}
            </div>
          ) : null}
          <div className="row" style={{ marginTop: 8 }}>
            <Button variant="ghost" onClick={() => setOpen(null)}>
              بستن
            </Button>
          </div>
        </div>
      ) : null}
      {priceFor ? (
        <PriceReceiptModal
          receipt={priceFor}
          onClose={() => setPriceFor(null)}
          onDone={() => {
            setPriceFor(null);
            if (open) void openPo(open.id);
            reload();
          }}
        />
      ) : null}
    </Card>
  );
}
